import React from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import {
  getVideosList,
  selectVideosState
} from 'modules/videos/ducks/selectors'
import { API_STATES } from 'utils/redux'
import VideoListItem from './VideoListItem'
import VideoListInfiniteScroller from './VideoListInfiniteScroller'
import VideoListSkeleton from './VideoListSkeleton'
import VideoListEmptyState from './VideoListEmptyState'
import VideoListLoadMoreLoader from './VideoListLoadMoreLoader'

export const VideoList = (): JSX.Element => {
  const videosList = useSelector(getVideosList)
  const videosState = useSelector(selectVideosState)

  const isListLoading =
    videosList.length === 0 &&
    (videosState === API_STATES.PRISTINE ||
      videosState === API_STATES.IN_PROGRESS)

  const isEmpty = videosList.length === 0 && videosState === API_STATES.DONE

  // Bottom hint while infinite scroll fetches the next page
  const isLoadingMore =
    videosList.length > 0 && videosState === API_STATES.IN_PROGRESS

  // @ts-ignore
  const renderVideos = videosList.map(video => (
    // @ts-ignore
    <VideoListItem key={`video-list-item-${video.id}`} video={video} />
  ))

  const renderContent = () => {
    if (isListLoading) return <VideoListSkeleton />
    if (isEmpty) return <VideoListEmptyState />
    return renderVideos
  }

  return (
    <Container>
      <VideosListContainer $isLoadingMore={isLoadingMore}>
        {renderContent()}
        <VideoListInfiniteScroller />
      </VideosListContainer>
      {/* Overlay stays in the visible viewport — not below the scroll fold */}
      {isLoadingMore && (
        <LoadMoreOverlay>
          <VideoListLoadMoreLoader />
        </LoadMoreOverlay>
      )}
    </Container>
  )
}

export default VideoList

const Container = styled.div`
  position: relative;
  margin-top: 24px;
  flex-grow: 1;
  max-height: calc(100% - 108px);
`

const VideosListContainer = styled.div<{ $isLoadingMore: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  overflow-y: auto;
  padding-bottom: ${({ $isLoadingMore }) => ($isLoadingMore ? '40px' : '0')};
`

const LoadMoreOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(
    to top,
    ${({ theme }) => theme.colors.backgrounds.main} 55%,
    transparent
  );
`
